import { CardContent, useTheme } from '@mui/material';
import { MprcCard, MprcContainer, MprcInfiniteLoading, MprcRequestErrorReport } from 'components';
import { FeedItemDto, FeedType } from 'generated/CoreApi_v1';
import { useWindowSize } from "hooks/useWindowSize";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { useGobackOrKnowledge } from 'views/common/hooks/useGobackOrHome';
import { CommentBox } from 'views/knowledge/components/comment/CommentBox';
import { RightPanelBreakpoints } from 'views/knowledge/feed/components/FeedContainer';
import { PublicationContentBox } from "views/knowledge/publication/pages/components/PublicationContentBox";
import { RightPanelContentPage } from '../common/RightPanelContentPage';
import { PublicationCardHeader } from '../components/PublicationCardHeader';
import { PostThreads } from './components/PostThreads';

////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId: string;
    openRightPanel: boolean;
    rightPanelBreakpoint: RightPanelBreakpoints;
    onCloseRightPanel: (value: boolean) => void;
}

////////////////////////////////////////////////////////////////////////////////////////////////

export const PostPlaybackViewer: React.FC<IProps> = (props) => {

    const gobackOrHome = useGobackOrKnowledge();
    const { publicationId, openRightPanel, onCloseRightPanel } = props;

    const api = useCoreApi();
    const location = useLocation();
    const theme = useTheme();
    const { width } = useWindowSize({ wait: 50 });

    const [loading, setLoading] = useState<boolean>(true);
    const [publication, setPublication] = useState<FeedItemDto>();
    const [viewError, setViewError] = useState<any>();

    ///
    const load = (publicationId: string) => {
        let markRead = true;

        // Se a solicitação foi realizada a partir do Feed de Histórico,
        // não deve marcar a Publicação como 'Lida', para não alterar o histórico de visualização.
        if (location.state?.hasOwnProperty("previousPathname")) {
            const previousPathname = location.state.previousPathname as string;
            markRead = !previousPathname.startsWith(KnowledgePaths.FeedHistory);
        }

        setLoading(true);
        api.v1.knowledgeFeed.getFeedItem({ publicationId, markRead })
            .then((result) => {
                setPublication(result.data);
                setLoading(false);
            })
            .catch((error: any) => {
                setViewError(error);
                setLoading(false);
            });
    };

    ///
    const onDataItemChanged = (data: Partial<FeedItemDto>) => {
        setPublication(prevState => {
            return { ...prevState!, ...data };
        });
    };

    useEffect(() => {
        if (publicationId != null) {
            load(publicationId);
        }
    }, [publicationId]);

    useEffect(() => {
        if (openRightPanel && width > theme.breakpoints.values["md"])
            onCloseRightPanel(false);
    }, [width]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderPublication = useCallback(() => (
        <PublicationCardHeader
            feedType={FeedType.PlaybackContentListPage}
            publication={publication!}
            onNeedResetRowHeight={() => { }}
            onReload={() => load(publicationId!)}
            onFeedItemChanged={(data) => {
                if (data.deletedOn) gobackOrHome();
                else onDataItemChanged(data);
            }}
        />
    ), [publication]);

    const RenderThread = useCallback(() => {
        return <PostThreads publicationId={publicationId!} onError={e => setViewError(e)}></PostThreads>;
    }, [publicationId]);

    const RenderComment = useCallback(() => {
        return (
            <MprcCard>
                <CardContent>
                    <CommentBox publicationId={publicationId!} publicationTitle={publication?.title ?? ""} readOnly={publication?.readOnly ?? false} />
                </CardContent>
            </MprcCard>
        );
    }, [publication]);

    return (
        <>
            {
                viewError != undefined
                    ? <MprcRequestErrorReport error={viewError} />
                    : loading
                        ? <MprcInfiniteLoading visible />
                        : publication != undefined &&
                        <PublicationContentBox
                            openRightPanel={openRightPanel}
                            maxWidth="lg"
                            onCloseRighPanel={() => onCloseRightPanel(false)}
                            content={
                                <MprcContainer>
                                    {RenderPublication()}
                                    {RenderThread()}
                                    {RenderComment()}
                                </MprcContainer>
                            }
                            rightPanel={publication != undefined ? RightPanelContentPage(publication) : undefined}
                        />
            }
        </>
    );
};
